import { recipeSrcSet } from "../lib/media";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Flame, Globe2 } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { PageHeader, Loader } from "../components/shared";

export default function Recipes() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("all");
  const [country, setCountry] = useState("all");
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get(`/content/recipes?lang=${lang}`).then(({ data }) => setData(data)).catch(() => {});
    Promise.all([api.get(`/content/ceia?lang=${lang}`), api.get("/config")]).then(([{ data: ceia }, { data: cfg }]) => {
      const names = Object.fromEntries((cfg.countries || []).map((c) => [c.code, c.name?.[lang] || c.name?.pt || c.code]));
      const order = ["BR", "PT", "US", "ES", "FR", "IT"];
      setCountries(Object.entries(ceia.typical_menus || {}).map(([code, m]) => ({ code, flag: m.flag, name: names[code] || code, title: m.title })).sort((a, b) => order.indexOf(a.code) - order.indexOf(b.code)));
    }).catch(() => {});
  }, [lang]);

  if (!data) return <Loader />;
  const recipes = data.recipes.filter((r) => (cat === "all" || r.category === cat) && (country === "all" || r.typical?.[country]));
  const flagsOf = (r) => countries.filter((c) => r.typical?.[c.code]);

  return (
    <div>
      <PageHeader title={t("recipes.title")} testid="recipes-header" />
      <div className="mb-8 flex flex-wrap gap-2">
        <button onClick={() => setCat("all")} className={`rounded-full px-4 py-2 text-sm transition ${cat === "all" ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>{t("recipes.all")}</button>
        {data.categories.map((c) => (
          <button key={c.id} data-testid={`recipe-cat-${c.id}`} onClick={() => setCat(c.id)} className={`rounded-full px-4 py-2 text-sm transition ${cat === c.id ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {countries.length > 0 && (
        <div className="-mt-4 mb-8 flex flex-wrap items-center gap-2" data-testid="recipes-country-filter">
          <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-xmas-slate"><Globe2 className="h-3.5 w-3.5 text-xmas-gold" /> {t("recipes.typical_by_country")}</span>
          <button onClick={() => setCountry("all")} data-testid="recipe-country-all" className={`rounded-full px-3 py-1.5 text-xs transition ${country === "all" ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>{t("recipes.all_countries")}</button>
          {countries.map((c) => (
            <button key={c.code} data-testid={`recipe-country-${c.code}`} onClick={() => setCountry(c.code)} title={c.title}
              className={`rounded-full px-3 py-1.5 text-xs transition ${country === c.code ? "btn-gold" : "glass text-xmas-slate hover:text-xmas-snow"}`}>
              {c.flag} {c.name}
            </button>
          ))}
        </div>
      )}

      {recipes.length === 0 && <p className="text-sm text-xmas-slate" data-testid="recipes-empty">{t("recipes.empty_filter")}</p>}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {recipes.map((r) => (
          <div key={r.id} className="card-lift group overflow-hidden rounded-2xl border border-xmas-gold/15 bg-xmas-navycard" data-testid={`recipe-card-${r.id}`}>
            <Link to={`/app/recipes/${r.id}`} className="block">
              <div className="relative -mb-px h-44 overflow-hidden">
                <img src={r.image} srcSet={recipeSrcSet(r.image)} sizes="(max-width: 640px) 100vw, 400px" alt={r.name} loading="lazy" decoding="async" className="block h-full w-full object-cover transition duration-700 group-hover:scale-110" />
                <div className="absolute -inset-px bg-gradient-to-t from-xmas-navycard to-transparent" />
                {flagsOf(r).length > 0 && (
                  <span className="absolute left-3 top-3 rounded-full border border-xmas-gold/40 bg-xmas-navy/80 px-2 py-0.5 text-[11px] font-semibold text-xmas-gold backdrop-blur" data-testid={`recipe-typical-${r.id}`}>
                    {flagsOf(r).map((c) => c.flag).join(" ")} {t("recipes.typical_badge")}
                  </span>
                )}
              </div>
            </Link>
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <Link to={`/app/recipes/${r.id}`}>
                  <h3 className="font-display text-lg font-semibold text-xmas-snow hover:text-xmas-gold">{r.name}</h3>
                </Link>
              </div>
              <p className="mt-1 line-clamp-2 text-sm text-xmas-slate">{r.description}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-xmas-slate">
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{r.time_minutes}{t("recipes.min")}</span>
                <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5" />{t(`diff.${r.difficulty}`)}</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{r.servings}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
